export type Post = {
  slug: string;
  title: string;
  /** <title> SEO complet (<= 60 caractères, suffixe inclus) */
  seoTitle?: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  cover: string;
  tldr?: string[];
  content: { type: "h2" | "h3" | "p" | "ul" | "quote"; text?: string; items?: string[] }[];
  faq?: { q: string; a: string }[];
  /** Article hérité, conservé pour les visiteurs mais exclu de l'index Google. */
  noindex?: boolean;
};

/**
 * Articles fusionnés dans l'article pilier « prix DJ mariage ».
 * Les anciennes URL sont redirigées en 301 pour conserver leur autorité.
 */
export const MERGED_POSTS: Record<string, string> = {
  "combien-coute-un-dj-pour-mariage-provence-2026": "prix-dj-mariage-provence-guide-complet-2026",
  "prix-dj-mariage-vaucluse-2026": "prix-dj-mariage-provence-guide-complet-2026",
  // Doublon local fusionné dans l'article de référence sur Cavaillon.
  "dj-cavaillon-mariage-animation-2": "dj-cavaillon-pour-mariage-animation-parfaite",
};


export const POSTS: Post[] = [
  {
    slug: "prix-dj-mariage-provence-guide-complet-2026",
    seoTitle: "Prix DJ mariage Provence : guide 2026 | DJ Val",
    title: "Prix DJ mariage : le guide complet 2026 (Provence, Vaucluse & Luberon)",
    excerpt:
      "Combien coûte vraiment un DJ mariage en Provence ? Tarifs 2026 détaillés, ce qui fait varier le prix, et comment repérer un vrai professionnel — par un DJ qui anime 200+ mariages depuis 20 ans.",
    date: "2026-07-26",
    readTime: "9 min",
    category: "Tarifs",
    cover: "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=72&auto=format&fit=crop",
    tldr: [
      "Fourchette réelle 2026 : 900 € à 2 800 € pour un mariage de 8 à 12 h en Provence.",
      "Prix moyen constaté DJ pro Vaucluse : 1 490 € à 1 900 € (formule complète sono + LED).",
      "Un DJ à moins de 700 € n'a ni assurance RC pro, ni SACEM, ni matériel de secours : le risque n'est pas le prix, c'est la soirée.",
      "5 postes de coût déterminants : durée, distance, taille du parc son, effets spéciaux, cérémonie laïque.",
    ],
    content: [
      { type: "p", text: "« Combien pour un DJ mariage ? » C'est la première question que reçoivent tous les prestataires. Réponse honnête : entre 900 € et 2 800 € en Provence en 2026, selon la durée, le matériel et les options. Voici la grille complète, poste par poste, sans jargon." },
      { type: "h2", text: "Prix DJ mariage 2026 : la grille de référence en Provence" },
      { type: "p", text: "Basé sur 200+ prestations réelles animées entre le Vaucluse, les Bouches-du-Rhône, le Gard, le Luberon et les Alpilles, voici les fourchettes que vous rencontrerez sur le marché." },
      { type: "ul", items: [
        "Formule Essentielle (6 h, sono + éclairage de base, jusqu'à 80 invités) : 690 € à 1 200 €",
        "Formule Signature (8 à 10 h, sono Line Array, LED, animation cocktail, jusqu'à 150 invités) : 1 490 € à 1 900 €",
        "Formule Prestige (10 à 12 h, régie Pioneer Opus Quad, lyres, lasers, photobooth) : 2 000 € à 2 800 €",
        "Options : étincelles froides (150–350 €), machine à fumée lourde (120–200 €), CO₂ (180–280 €), cérémonie laïque sonorisée (250–450 €), guirlandes guinguette (150–300 €).",
      ] },
      { type: "h2", text: "Pourquoi de tels écarts ? Les 5 postes de coût décisifs" },
      { type: "h3", text: "1. La durée réelle de la prestation" },
      { type: "p", text: "Un DJ mariage professionnel arrive 3 heures avant l'ouverture des portes pour installer, faire la balance, briefer le traiteur. Il repart après le démontage complet, souvent vers 4 h du matin. Une prestation de « 8 h » représente en réalité 12 à 14 h de présence. Un tarif horaire honnête tourne donc autour de 100 à 150 € nets de l'heure travaillée." },
      { type: "h3", text: "2. La distance au lieu" },
      { type: "p", text: "En Provence, la plupart des DJ pro incluent 60 à 100 km aller-retour. Au-delà, comptez 0,80 à 1,20 €/km, plus une éventuelle nuitée si le lieu est isolé. Chez DJ Val, le déplacement est offert dans un rayon de 80 km autour de Cavaillon — soit Avignon, Aix-en-Provence, Arles, Nîmes, Uzès, Salon-de-Provence, Orange et tout le Luberon." },
      { type: "h3", text: "3. La taille du parc son" },
      { type: "p", text: "Sonoriser 60 personnes en intérieur ou 250 en extérieur, ce n'est pas la même prestation. Un vrai pro dimensionne : 1 kW acoustique par tranche de 50 invités, doublé en secours. Cela veut dire enceintes Line Array RCF/JBL, caissons de basses, et micros HF Shure. Ce matériel loué séparément coûte déjà 400 à 800 €/jour." },
      { type: "h3", text: "4. Les effets spéciaux et la lumière" },
      { type: "p", text: "Un kit LED basique (4 jeux) est inclus dans toutes les formules pro. Au-delà : lyres à faisceau, lasers, machine à brouillard lourd, étincelles froides, CO₂, guirlandes guinguette — chaque effet ajoute 100 à 350 €. La règle : n'ajoutez que ce qui sert le moment (étincelles pour l'entrée des mariés, CO₂ pour un tempo de dancefloor)." },
      { type: "h3", text: "5. Prestations annexes : cérémonie laïque, photobooth, apéro live" },
      { type: "p", text: "Sonoriser une cérémonie laïque (micros HF, retours, playlist croisée) ajoute 250 à 450 €. Un photobooth avec impressions illimitées : 350 à 550 € pour la soirée. Un set live guitare-voix cocktail : 400 à 700 € selon l'artiste partenaire." },
      { type: "h2", text: "Pourquoi se méfier des DJ mariage à moins de 700 €" },
      { type: "p", text: "Un DJ pro déclaré paie : URSSAF, SACEM (redevance obligatoire pour diffuser de la musique en événementiel), RC pro (2 M€ minimum), amortissement matériel (15 à 25 k€ de parc). Un devis à 500 € tout compris signifie l'un des trois scénarios suivants." },
      { type: "ul", items: [
        "Statut non déclaré : aucune facture, aucune couverture. En cas de casse, blessure, panne, vous êtes seul.",
        "Matériel grand public : enceintes de home cinéma, contrôleur d'entrée de gamme, un seul micro. Résultat : son saturé aux basses, feedbacks, coupures.",
        "Pas de backup : une seule enceinte, une seule table de mixage, un seul micro. Le jour d'un mariage, la panne n'est jamais une option acceptable.",
      ] },
      { type: "quote", text: "Le vrai coût d'un DJ, ce n'est pas ce que vous payez. C'est ce que vous perdez si votre soirée s'arrête à 23 h parce que le matériel a lâché." },
      { type: "h2", text: "Ce qui doit obligatoirement figurer dans un devis DJ mariage sérieux" },
      { type: "ul", items: [
        "Numéro SIRET et attestation d'assurance RC pro à jour",
        "Détail du matériel : marques et modèles précis (Pioneer CDJ-3000, DJM-A9, RCF Line Array, Shure HF)",
        "Durée exacte de la prestation, heure d'arrivée et de démontage",
        "Zone incluse dans le déplacement, tarif au-delà",
        "Acompte demandé (30 % standard) et conditions d'annulation écrites",
        "Un rendez-vous découverte avant signature (visio ou en présentiel)",
      ] },
      { type: "h2", text: "Prix DJ mariage par ville en Provence : les tendances 2026" },
      { type: "ul", items: [
        "Avignon : marché tendu haute saison (mai à septembre). Tarif moyen constaté : 1 500 à 2 000 € pour une formule complète.",
        "Aix-en-Provence : plus premium (nombreux domaines d'exception). Moyenne : 1 700 à 2 400 €.",
        "Arles & Alpilles : forte saisonnalité, prix similaires à Avignon pour un domaine à Saint-Rémy ou aux Baux.",
        "Luberon (Gordes, Ménerbes, Bonnieux) : logistique complexe (lieux isolés), prévoir 1 800 à 2 500 €.",
        "Cavaillon, Carpentras, L'Isle-sur-la-Sorgue : marché local, tarifs autour de 1 400 à 1 800 €.",
      ] },
      { type: "h2", text: "Comment DJ Val fixe ses tarifs (transparence totale)" },
      { type: "p", text: "Trois formules claires, publiées sur le site, ajustées à la taille de votre événement. Aucun supplément caché, aucun frais de dossier, aucune ligne « déplacement » surprise dans les 80 km. Le devis reçu sous 24 h est ferme et détaillé, TVA incluse (auto-entreprise). Acompte 30 % à la signature, solde 7 jours avant l'événement." },
      { type: "ul", items: [
        "Essentielle — 690 € : petit événement (60 pers, 6 h)",
        "Signature — 1 490 € : mariage complet (150 pers, 10 h)",
        "Prestige — 2 190 € : mariage premium (200+ pers, 12 h, effets)",
      ] },
    ],
    faq: [
      { q: "Quel est le prix moyen d'un DJ mariage en Provence en 2026 ?", a: "Le prix moyen constaté pour un DJ mariage professionnel en Provence en 2026 se situe entre 1 490 € et 1 900 € pour une formule complète (sonorisation Line Array, éclairage LED, 8 à 10 heures de prestation). En dessous de 900 €, il s'agit rarement d'un professionnel déclaré avec RC pro et matériel de secours." },
      { q: "Combien coûte un DJ mariage à Avignon ?", a: "À Avignon, le tarif constaté pour un DJ mariage professionnel varie de 1 500 € à 2 000 € pour une prestation complète de 8 à 10 heures avec sonorisation Line Array et éclairage LED. Les formules premium (Pioneer Opus Quad, effets spéciaux, photobooth) atteignent 2 000 à 2 800 €." },
      { q: "Pourquoi un DJ mariage coûte-t-il aussi cher ?", a: "Le tarif reflète 12 à 14 heures de présence (installation, prestation, démontage), un investissement matériel de 15 000 à 25 000 € amorti sur chaque événement, l'assurance RC pro (2 M€), les redevances SACEM obligatoires, ainsi que la préparation en amont (rendez-vous découverte, playlist, trame). Un DJ pro n'est pas une playlist Spotify branchée sur une enceinte." },
      { q: "Que doit contenir un devis DJ mariage sérieux ?", a: "Un devis DJ mariage professionnel mentionne : numéro SIRET, attestation RC pro à jour, marques et modèles précis du matériel, durée exacte de prestation, zone de déplacement incluse, montant de l'acompte (30 % standard), conditions d'annulation écrites, et propose un rendez-vous découverte avant signature." },
      { q: "Quel acompte demande un DJ mariage professionnel ?", a: "L'acompte standard est de 30 % du montant total, versé à la signature du contrat. Le solde est réglé 7 jours avant l'événement. Toute demande d'acompte supérieure à 50 % ou d'un paiement 100 % à la signature est atypique et doit alerter." },
      { q: "Le prix inclut-il la SACEM ?", a: "Chez DJ Val, oui : la redevance SACEM (diffusion musicale en événementiel privé, forfait environ 100 à 180 € selon le nombre d'invités) est intégrée dans le tarif de la formule Signature et Prestige. C'est un poste obligatoire souvent oublié par les DJ amateurs, ce qui peut valoir une amende à l'organisateur." },
    ],
  },

  {
    slug: "mariage-2026-tendances-musicales",
    seoTitle: "Tendances musicales mariage 2026 | DJ Val",
    title: "Mariage 2026 : tendances musicales et ambiances qui font vibrer les dancefloors",
    excerpt:
      "Tour d'horizon des styles, des sets et des ambiances qui dominent les mariages 2026 en Provence — du cocktail à la dernière danse.",
    date: "2026-03-25",
    readTime: "7 min",
    category: "Tendances",
    cover: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=72&auto=format&fit=crop",
    content: [
      { type: "p", text: "2026 confirme la fin des playlists figées et l'avènement du DJ-curateur. Les mariés veulent une ambiance signée, un fil rouge musical qui raconte leur histoire." },
      { type: "h2", text: "Le retour des années 2000" },
      { type: "p", text: "Britney, Beyoncé, R&B chic : la génération qui se marie aujourd'hui a 30-35 ans et nostalgise ses années collège-lycée. Un set Y2K en fin de soirée fait toujours mouche." },
      { type: "h2", text: "Le mix afro / latino" },
      { type: "p", text: "Afrobeats, Amapiano, Reggaeton chic : les invités multiculturels adorent cette ouverture. À doser, pas à imposer." },
      { type: "h2", text: "Cocktail jazz manouche / chanson française" },
      { type: "p", text: "Pour le vin d'honneur en Provence, rien ne bat un duo guitare-voix ou une playlist jazz manouche soigneusement curée." },
      { type: "ul", items: ["Set d'entrée live ou DJ ambiance lounge", "Repas : crooners, soul, French touch", "21h-23h : 80's, 90's, fédérateurs", "Minuit-2h : club, house, hip-hop", "After 3h : retour aux fondamentaux pour les derniers fidèles"] },
    ],
  },
  {
    slug: "combien-coute-un-dj-pour-mariage-provence-2026",
    seoTitle: "Combien coûte un DJ mariage en Provence ? | DJ Val",
    title: "Combien coûte un DJ pour mariage en Provence en 2026 ?",
    excerpt: "Fourchette de prix, postes de coût, options à connaître. La transparence totale sur le tarif d'un DJ mariage Provence en 2026.",
    date: "2026-03-26",
    readTime: "6 min",
    category: "Conseils",
    cover: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1200&q=72&auto=format&fit=crop",
    content: [
      { type: "p", text: "Question récurrente : combien faut-il prévoir pour un DJ professionnel en Provence ? Voici les fourchettes réelles, sans langue de bois." },
      { type: "h2", text: "Formules courantes" },
      { type: "ul", items: ["Essentielle (6h, sono + lumière de base) : 900 à 1300 €", "Signature (8h, lumière LED, animation cocktail) : 1400 à 1900 €", "Prestige (10h, lyres, lasers, photobooth) : 2000 à 2800 €"] },
      { type: "h2", text: "Ce qui fait varier le prix" },
      { type: "ul", items: ["Distance du lieu", "Nombre d'invités (sono dimensionnée en conséquence)", "Cérémonie laïque incluse ou non", "Effets spéciaux : CO₂, confettis, machine à bulles, guirlandes guinguette", "Photobooth"] },
      { type: "h2", text: "Pourquoi se méfier du DJ à 500 €" },
      { type: "p", text: "Pas d'assurance, matériel grand public, pas de SACEM, pas de backup. Le jour de votre mariage, ce n'est pas le moment de découvrir que votre prestataire bricole." },
    ],
  },
  {
    slug: "comment-choisir-le-dj-ideal-pour-votre-mariage",
    seoTitle: "Comment choisir le DJ idéal de son mariage | DJ Val",
    title: "Comment choisir le DJ idéal pour votre mariage",
    excerpt: "Les 7 critères qui distinguent un vrai pro d'un DJ amateur — et les questions à poser absolument avant de signer.",
    date: "2025-12-27",
    readTime: "5 min",
    category: "Conseils",
    cover: "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=1200&q=72&auto=format&fit=crop",
    content: [
      { type: "h2", text: "1. Le feeling humain" },
      { type: "p", text: "Vous allez passer une demi-journée avec cette personne. Le rendez-vous découverte est essentiel." },
      { type: "h2", text: "2. Les références vérifiables" },
      { type: "p", text: "Demandez 5 contacts de mariés récents. Un vrai pro vous les donne sans hésiter." },
      { type: "h2", text: "3. La SACEM et l'assurance" },
      { type: "p", text: "SACEM, RC pro, attestation à jour : trois indispensables. Sinon, fuyez." },
      { type: "h2", text: "4. Le matériel" },
      { type: "p", text: "Marques pro (Pioneer, Shure, RCF), backup complet, câbles propres. Visitez son local si possible." },
      { type: "h2", text: "5. La trame personnalisée" },
      { type: "p", text: "Un bon DJ vous fait remplir un questionnaire détaillé : must-play, no-go, moments forts, surprises." },
    ],
  },
  {
    slug: "pourquoi-engager-un-dj-professionnel-pour-votre-mariage",
    seoTitle: "Pourquoi engager un DJ professionnel | DJ Val",
    title: "Pourquoi engager un DJ professionnel pour votre mariage",
    excerpt: "iPod sur enceinte Bluetooth ou DJ pro : la différence se joue à 22h45, quand la piste se remplit ou se vide.",
    date: "2025-12-27",
    readTime: "4 min",
    category: "Conseils",
    cover: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200&q=72&auto=format&fit=crop",
    content: [
      { type: "p", text: "Tentation classique : confier la musique à un cousin et brancher une enceinte. Bilan dans 90% des cas : piste vide à 23h, ambiance qui retombe, soirée écourtée." },
      { type: "h2", text: "Le DJ lit votre dancefloor" },
      { type: "p", text: "Un DJ pro voit qui danse, qui hésite, qui regarde son téléphone. Il enchaîne pour faire monter la température et la maintenir." },
      { type: "h2", text: "L'expérience du tempo" },
      { type: "p", text: "Savoir quand sortir le slow, quand caler le tube fédérateur, quand jouer la carte du souvenir : c'est un métier." },
    ],
  },
  {
    slug: "guirlandes-guinguette-ambiance-festive",
    noindex: true,
    seoTitle: "Guirlandes guinguette pour un mariage | DJ Val",
    title: "Guirlandes guinguette : l'accessoire incontournable pour vos mariages",
    excerpt: "Pourquoi les guirlandes ampoules à filaments transforment radicalement l'ambiance d'un mariage en Provence.",
    date: "2025-12-27",
    readTime: "3 min",
    category: "Décoration",
    cover: "https://images.unsplash.com/photo-1530023367847-a683933f4172?w=1200&q=72&auto=format&fit=crop",
    content: [
      { type: "p", text: "C'est probablement l'élément déco qui transforme le plus une cour de mas en lieu magique. Et c'est étonnamment abordable." },
      { type: "h2", text: "Pourquoi ça marche" },
      { type: "p", text: "Lumière chaude, ambiance intime, photos sublimées. Les guirlandes guinguette créent un plafond lumineux qui contient l'espace et invite à danser." },
    ],
  },
  {
    slug: "dj-cavaillon-pour-mariage-animation-parfaite",
    seoTitle: "DJ Cavaillon mariage : l'animation parfaite | DJ Val",
    title: "DJ Cavaillon pour mariage : l'animation parfaite",
    excerpt: "Pourquoi un DJ local à Cavaillon change votre expérience — connaissance des lieux, du terroir, de votre public.",
    date: "2025-12-27",
    readTime: "4 min",
    category: "Local",
    cover: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1200&q=72&auto=format&fit=crop",
    content: [
      { type: "p", text: "Cavaillon, capitale du melon mais aussi épicentre de magnifiques domaines de mariage. Un DJ local change tout." },
      { type: "h2", text: "Connaissance des domaines" },
      { type: "p", text: "Mas des Comtes de Provence, Domaine de Tara : on sait à l'avance où installer, comment optimiser l'acoustique, où placer les enceintes pour le cocktail." },
      { type: "h2", text: "Ce que m'ont appris 200 soirées" },
      { type: "p", text: "200 mariages, ça forme un œil. Voici ce qui revient systématiquement chez les couples dont la soirée est restée gravée." },
      { type: "h2", text: "Ils ont fait confiance" },
      { type: "p", text: "Pas de micro-management de la playlist, mais un brief clair en amont. Liberté + cadre = magie." },
      { type: "h2", text: "Ils ont mangé tôt" },
      { type: "p", text: "Repas terminé à 22h30 max = la piste démarre dans la foulée. Repas à 1h = soirée morte." },
    ],
  },
  {
    slug: "faites-de-votre-mariage-une-fete-inoubliable",
    seoTitle: "Réussir l'animation musicale de son mariage | DJ Val",
    title: "Faites de votre mariage une fête inoubliable grâce à une animation musicale exceptionnelle",
    excerpt: "Les 3 piliers d'une soirée mémorable : sonorisation soignée, mise en lumière maîtrisée, sélection musicale ciselée.",
    date: "2025-12-27",
    readTime: "5 min",
    category: "Conseils",
    cover: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=72&auto=format&fit=crop",
    content: [
      { type: "h2", text: "Le son" },
      { type: "p", text: "Pas trop fort au cocktail, parfait au repas, club à minuit. Le volume raconte une histoire." },
      { type: "h2", text: "La lumière" },
      { type: "p", text: "Cocktail en lumière chaude, dîner intimiste, dancefloor club : trois ambiances, une scénographie." },
      { type: "h2", text: "La sélection" },
      { type: "p", text: "Hits qui fédèrent, pépites qui surprennent, séquences qui émeuvent." },
    ],
  },
  {
    slug: "tendances-mariage-2026",
    seoTitle: "Tendances mariage 2026 | DJ Val",
    title: "Tendances mariage 2026 en Provence",
    excerpt: "Cérémonie laïque en plein air, brunch du lendemain, photobooth obligatoire : ce qui s'installe durablement.",
    date: "2026-08-25",
    readTime: "5 min",
    category: "Tendances",
    cover: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1200&q=72&auto=format&fit=crop",
    content: [
      { type: "p", text: "Certaines tendances passent. D'autres s'installent. Voici celles qui dominent toujours en 2026." },
      { type: "h2", text: "La cérémonie laïque outdoor" },
      { type: "p", text: "Symbole de personnalisation, elle structure désormais 8 mariages sur 10 en Provence." },
      { type: "h2", text: "Le photobooth obligatoire" },
      { type: "p", text: "Devenu une institution. Vos invités repartent avec leur photo, vous gardez l'album." },
    ],
  },
  {
    slug: "lumiere-led-mobile",
    seoTitle: "Lumière LED mobile : à quoi ça sert ? | DJ Val",
    title: "Qu'est-ce qu'une lumière LED mobile ?",
    excerpt: "Démystification rapide : lyre, par LED, scanner, laser… ce que chaque machine apporte à votre soirée.",
    date: "2026-08-25",
    readTime: "3 min",
    category: "Matériel",
    cover: "/__l5e/assets-v1/cf6fa65e-6b39-4acc-be3e-1544a47a1d36/lumiere-led-mobile.webp",
    content: [
      { type: "h2", text: "Lyre beam" },
      { type: "p", text: "Faisceau lumineux fin, mobile, ultra rapide. La star des dancefloors." },
      { type: "h2", text: "Par LED" },
      { type: "p", text: "Wash de couleur, statique ou mobile. Idéal pour habiller un mur, un plafond, un dancefloor." },
    ],
  },
  {
    slug: "prix-dj-mariage-vaucluse-2026",
    seoTitle: "Prix DJ mariage Vaucluse 2026 | DJ Val",
    title: "Prix DJ mariage Vaucluse 2026 : combien prévoir vraiment (guide complet)",
    excerpt:
      "Fourchettes de prix réelles, ce qui fait varier le tarif, les pièges à éviter et la grille DJ Val pour un mariage dans le Vaucluse en 2026.",
    date: "2026-04-02",
    readTime: "9 min",
    category: "Tarifs",
    cover: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=72&auto=format&fit=crop",
    tldr: [
      "Budget DJ mariage dans le Vaucluse en 2026 : entre 900 € et 2 800 € selon la formule.",
      "Tarif moyen observé pour 100 invités sur 8 h : 1 490 €.",
      "Les vrais postes de coût : durée, matériel, déplacement, cérémonie laïque, effets spéciaux.",
      "DJ Val propose 3 formules transparentes — Essentielle, Signature, Prestige — depuis Cavaillon.",
    ],
    content: [
      { type: "p", text: "Faire chiffrer un DJ pour son mariage dans le Vaucluse en 2026 reste un exercice opaque pour beaucoup de couples. Devis qui varient du simple au quadruple, prestations difficiles à comparer, options cachées. Ce guide met tout à plat, sans langue de bois, avec les vraies fourchettes pratiquées dans le département." },
      { type: "h2", text: "Fourchette de prix dans le Vaucluse en 2026" },
      { type: "p", text: "Pour un mariage entre 60 et 150 invités dans le Vaucluse, un DJ professionnel se positionne entre 900 € et 2 800 €. Cette fourchette large s'explique par la durée, le matériel mobilisé, le déplacement et les options. Voici les paliers observés." },
      { type: "ul", items: [
        "Formule Essentielle (5-6 h, sono compacte, lumière LED de base) : 690 € à 1 190 €",
        "Formule Signature (8 h, sono pro + lyres LED, micro HF cérémonie) : 1 490 € à 1 890 €",
        "Formule Prestige (10 h, sono multi-zones, lasers, machine brouillard lourd, technicien) : 2 190 € à 2 800 €",
      ] },
      { type: "h2", text: "Ce qui fait monter ou descendre le devis" },
      { type: "h3", text: "1. La durée de prestation" },
      { type: "p", text: "Chaque heure supplémentaire coûte généralement entre 80 € et 150 €. Une animation 6 h ne mobilise pas le même matériel qu'une prestation 10 h avec sonorisation cérémonie en plus." },
      { type: "h3", text: "2. Le nombre d'invités et la taille du lieu" },
      { type: "p", text: "Sonoriser 200 personnes sous chapiteau ou 60 personnes dans un caveau ne demande pas le même parc d'enceintes. Un DJ pro dimensionne son matériel à votre lieu, pas l'inverse." },
      { type: "h3", text: "3. Le déplacement" },
      { type: "p", text: "Depuis Cavaillon, un mariage à Avignon, Aix ou Saint-Rémy n'engendre quasi aucun supplément. Au-delà de 50 km, comptez un forfait déplacement transparent annoncé sur le devis." },
      { type: "h3", text: "4. Les options qui font la différence" },
      { type: "ul", items: [
        "Cérémonie laïque sonorisée (micros HF, retours) : +150 à +250 €",
        "Photobooth avec impressions instantanées : +250 à +450 €",
        "Étincelles froides ouverture de bal : +120 à +180 €",
        "Machine à fumée lourde + lasers : +200 à +400 €",
        "Guirlandes guinguette installées : +180 à +300 €",
      ] },
      { type: "h2", text: "Pourquoi se méfier des devis à moins de 700 €" },
      { type: "p", text: "À ce niveau de prix, vous tombez quasi systématiquement sur un amateur sans SACEM, sans assurance responsabilité civile professionnelle, sans matériel de secours et sans expérience scénique. Le jour du mariage, ce n'est pas le moment de découvrir que la table de mixage saute ou que le DJ ne sait pas gérer un dancefloor multi-générationnel." },
      { type: "quote", text: "Un DJ professionnel, ce n'est pas un cousin qui branche Spotify sur une enceinte. C'est un métier, un parc matériel à 30 000 €, une lecture de salle, une responsabilité juridique." },
      { type: "h2", text: "Le tarif moyen d'un mariage dans le Vaucluse en 2026" },
      { type: "p", text: "Sur les 80 mariages animés par DJ Val ces deux dernières années dans le Vaucluse, le ticket moyen s'établit à 1 490 € pour une animation 8 h avec sonorisation cérémonie incluse. C'est le prix de la sérénité totale : matériel pro doublé, repérage technique, rendez-vous préparatoire, suivi prestataires." },
      { type: "h2", text: "Comment réduire intelligemment l'addition" },
      { type: "ul", items: [
        "Réserver tôt (12 mois avant) — les meilleurs tarifs partent vite",
        "Choisir une date hors saison haute (oct-avr) : -10 à -20 %",
        "Cumuler sonorisation cérémonie + soirée chez le même prestataire",
        "Préciser dès le départ le nombre d'invités réel, pas une fourchette large",
      ] },
    ],
    faq: [
      { q: "Quel est le prix moyen d'un DJ mariage dans le Vaucluse en 2026 ?", a: "Comptez en moyenne 1 490 € pour une formule complète 8 h avec sonorisation cérémonie, pour 100 invités, dans un domaine standard du Vaucluse." },
      { q: "Faut-il payer un acompte ?", a: "Oui, généralement 30 % à la signature du contrat. Le solde est versé le jour de la prestation ou par virement la semaine qui suit." },
      { q: "Le déplacement est-il facturé en plus ?", a: "Pas dans un rayon de 80 km autour de Cavaillon. Au-delà, un forfait kilométrique transparent est ajouté au devis." },
      { q: "Que se passe-t-il en cas d'annulation ?", a: "Conditions précisées au contrat. L'acompte est généralement conservé, sauf cas de force majeure (santé, deuil) où une nouvelle date peut être trouvée." },
    ],
  },
  {
    slug: "top-15-domaines-mariage-luberon",
    seoTitle: "Top 15 domaines de mariage en Luberon 2026 | DJ Val",
    title: "Top 15 domaines mariage Luberon : la sélection 2026 d'un DJ qui les connaît tous",
    excerpt:
      "Bastides, mas, châteaux : la liste des 15 plus beaux lieux de réception du Luberon, vus de l'intérieur par un DJ qui y a animé des mariages.",
    date: "2026-04-05",
    readTime: "11 min",
    category: "Lieux",
    cover: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1200&q=72&auto=format&fit=crop",
    tldr: [
      "15 domaines du Luberon analysés par capacité, ambiance, contraintes acoustiques et accessibilité.",
      "Du mas intimiste 60 invités au château 250 couverts.",
      "Sélection honnête, basée sur 200+ mariages animés dans le secteur depuis 20 ans.",
    ],
    content: [
      { type: "p", text: "Le Luberon est l'une des destinations mariage les plus convoitées de France. Entre Gordes, Lourmarin, Bonnieux et Ménerbes, les lieux d'exception ne manquent pas. Mais chacun a ses spécificités, ses contraintes techniques, son ambiance. Voici la sélection de 15 domaines que DJ Val recommande, classés par capacité et style." },
      { type: "h2", text: "Les références haut de gamme (>180 invités)" },
      { type: "h3", text: "1. Coquillade Provence Resort & Spa — Gargas" },
      { type: "p", text: "Le summum du raffinement provençal. Hôtel 5★ avec terrasse panoramique sur le Luberon. Capacité jusqu'à 220 couverts. Acoustique soignée, équipe événementielle expérimentée. Idéal pour un mariage haut de gamme sans concession." },
      { type: "h3", text: "2. Bastide de Marie — Ménerbes" },
      { type: "p", text: "Maison de famille devenue hôtel intimiste. Privatisation complète possible, jardin clos, ambiance bohème chic. Limite sonore 95 dB en extérieur après 22h — un bon DJ sait composer avec." },
      { type: "h3", text: "3. Domaine de Capelongue — Bonnieux" },
      { type: "p", text: "Vue à 360° sur le Luberon, terrasses étagées, salle de réception pierre apparente. 180 couverts assis. Le coucher de soleil cocktail est mythique." },
      { type: "h2", text: "Les bastides d'exception (120-180 invités)" },
      { type: "h3", text: "4. La Bastide de Gordes" },
      { type: "p", text: "Position dominante exceptionnelle, terrasses panoramiques sur le village perché. Service événementiel rodé." },
      { type: "h3", text: "5. Domaine de Fontenille — Lauris" },
      { type: "p", text: "Domaine viticole 5★ rénové. Parc XVIIIᵉ, piscine miroir, salle voûtée pour la soirée. Très demandé, à réserver 18 mois à l'avance." },
      { type: "h3", text: "6. Mas de la Rose — Orgon" },
      { type: "p", text: "À la frontière Luberon / Alpilles. Mas en pierre, jardin labyrinthique, ambiance romantique. 140 couverts." },
      { type: "h2", text: "Les mas authentiques (80-120 invités)" },
      { type: "h3", text: "7. Mas des Comtes de Provence — Plan-d'Orgon" },
      { type: "p", text: "Cour intérieure majestueuse, dancefloor extérieur sous guirlandes guinguette. Acoustique généreuse." },
      { type: "h3", text: "8. Le Mas de la Chapelle — Cavaillon" },
      { type: "p", text: "Chapelle attenante pour cérémonie laïque ou religieuse, piscine, vue Luberon. 100 couverts." },
      { type: "h3", text: "9. Domaine de Tara — Vaucluse" },
      { type: "p", text: "Cadre nature préservé, mas en pierre, parc 5 hectares. Ambiance détendue, peu de contraintes." },
      { type: "h3", text: "10. Mas de Pierre — Saint-Paul-de-Vence (extension Luberon)" },
      { type: "p", text: "Pour ceux qui débordent côté Côte d'Azur. Service hôtelier 5★." },
      { type: "h2", text: "Les pépites intimistes (<80 invités)" },
      { type: "h3", text: "11. La Coquillade — version privatisée pavillon" },
      { type: "p", text: "Privatisation d'un pavillon pour mariage intime 50-60 personnes." },
      { type: "h3", text: "12. Domaine des Andéols — Saint-Saturnin-lès-Apt" },
      { type: "p", text: "Maisons d'architectes au cœur des vignes. Pour mariages design 60 couverts." },
      { type: "h3", text: "13. Le Phébus & Spa — Joucas" },
      { type: "p", text: "Relais & Châteaux. Vue Luberon, jardin terrasse, 70 couverts max." },
      { type: "h3", text: "14. Hôtel Crillon le Brave" },
      { type: "p", text: "Village hôtelier privatisable. Ambiance villageoise, charme fou." },
      { type: "h3", text: "15. Domaine de la Verrière — Goult" },
      { type: "p", text: "Vignoble + maisons d'hôtes. Privatisation totale, 80 invités, expérience exclusive." },
      { type: "h2", text: "Choisir son domaine : les 5 questions à se poser" },
      { type: "ul", items: [
        "Quelle est la capacité réelle pour danser (pas seulement dîner) ?",
        "Quelles sont les contraintes acoustiques (limiteur, horaire de coupure) ?",
        "Y a-t-il un repli intérieur en cas de pluie ?",
        "L'accès véhicules est-il facile (camion sono) ?",
        "L'alimentation électrique est-elle dimensionnée (16A vs 32A triphasé) ?",
      ] },
    ],
    faq: [
      { q: "Quel domaine du Luberon est le moins cher ?", a: "Les mas authentiques type Domaine de Tara ou Mas des Comtes de Provence proposent des locations entre 3 500 € et 6 000 € la privatisation week-end, soit moitié moins que les références 5★." },
      { q: "Faut-il un DJ qui connaît déjà le lieu ?", a: "Idéalement oui. Connaître l'acoustique, l'accès, l'alimentation électrique et les contraintes du domaine fait gagner un temps précieux le jour J." },
      { q: "Combien de temps à l'avance réserver un domaine du Luberon ?", a: "12 à 18 mois pour les références haut de gamme en haute saison. 8 à 12 mois pour les mas intimistes." },
    ],
  },
  {
    slug: "playlist-mariage-50-morceaux-qui-font-danser",
    seoTitle: "Playlist mariage : 50 morceaux qui font danser | DJ Val",
    title: "Playlist mariage : 50 morceaux qui font danser tout le monde (testés sur 500+ soirées)",
    excerpt:
      "La sélection ultime d'un DJ pro pour remplir le dancefloor à coup sûr — cocktail, repas, ouverture de bal, club et after.",
    date: "2026-04-08",
    readTime: "8 min",
    category: "Musique",
    cover: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200&q=72&auto=format&fit=crop",
    tldr: [
      "50 titres organisés par moment de la soirée.",
      "Validés sur 200+ mariages animés en 20 ans en Provence.",
      "Mix générations : grands-parents au premier rang, ados en deuxième ligne.",
    ],
    content: [
      { type: "p", text: "Un mariage qui danse jusqu'à 3 h du matin n'arrive pas par hasard. Derrière chaque dancefloor plein se cache une sélection chirurgicale : 10 morceaux mal choisis suffisent à vider une piste, 10 bien placés à la rallumer." },
      { type: "h2", text: "Cocktail (10 morceaux pour mettre l'ambiance sans imposer)" },
      { type: "ul", items: [
        "Norah Jones — Don't Know Why",
        "Stacey Kent — Postcard Lovers",
        "Jamie Cullum — These Are the Days",
        "Caro Emerald — That Man",
        "Pink Martini — Sympathique",
        "Michael Bublé — Save the Last Dance for Me",
        "Madeleine Peyroux — Dance Me to the End of Love",
        "Édith Piaf — La Vie en Rose (version cover)",
        "Sting — Englishman in New York",
        "Buena Vista Social Club — Chan Chan",
      ] },
      { type: "h2", text: "Repas (10 morceaux qui accompagnent sans couvrir les conversations)" },
      { type: "ul", items: [
        "Stevie Wonder — Isn't She Lovely",
        "Frank Sinatra — Fly Me to the Moon",
        "Etta James — At Last",
        "Ray Charles — Georgia on My Mind",
        "Marvin Gaye — Let's Get It On",
        "Daft Punk — Get Lucky",
        "Pharrell Williams — Happy",
        "Vianney — Beau-Papa",
        "Christophe Maé — Marcel",
        "Coldplay — Yellow",
      ] },
      { type: "h2", text: "Ouverture de bal (5 morceaux mythiques)" },
      { type: "ul", items: [
        "Ed Sheeran — Perfect",
        "John Legend — All of Me",
        "Etta James — At Last",
        "Christina Perri — A Thousand Years",
        "Elvis Presley — Can't Help Falling in Love",
      ] },
      { type: "h2", text: "Première heure de bal (10 fédérateurs intergénérationnels)" },
      { type: "ul", items: [
        "Earth Wind & Fire — September",
        "Stevie Wonder — Superstition",
        "Bee Gees — Stayin' Alive",
        "Queen — Don't Stop Me Now",
        "Michael Jackson — Billie Jean",
        "ABBA — Dancing Queen",
        "Whitney Houston — I Wanna Dance with Somebody",
        "Donna Summer — Hot Stuff",
        "Lipps Inc — Funkytown",
        "KC and the Sunshine Band — That's the Way",
      ] },
      { type: "h2", text: "Minuit-2h (10 morceaux pour faire péter le toit)" },
      { type: "ul", items: [
        "Daft Punk — One More Time",
        "Avicii — Wake Me Up",
        "David Guetta — Titanium",
        "The Weeknd — Blinding Lights",
        "Dua Lipa — Don't Start Now",
        "Beyoncé — Crazy in Love",
        "Black Eyed Peas — I Gotta Feeling",
        "Outkast — Hey Ya!",
        "Sister Sledge — We Are Family",
        "Stromae — Alors on danse",
      ] },
      { type: "h2", text: "After 3h (5 morceaux pour les derniers fidèles)" },
      { type: "ul", items: [
        "Oasis — Wonderwall",
        "Queen — Bohemian Rhapsody",
        "Journey — Don't Stop Believin'",
        "Pharrell Williams — Get Lucky",
        "Frank Sinatra — New York, New York (la classique de clôture)",
      ] },
      { type: "h2", text: "La règle d'or" },
      { type: "p", text: "Une playlist n'est qu'un point de départ. Le vrai métier du DJ, c'est de lire la salle en temps réel et d'adapter l'ordre, le tempo, l'intensité. Une playlist Spotify ne remplacera jamais une oreille pro." },
    ],
    faq: [
      { q: "Puis-je imposer ma propre playlist au DJ ?", a: "Oui, et c'est même recommandé pour les must-play et no-go. Mais laissez la marge de manœuvre au DJ pour l'ordre et les transitions — c'est son métier." },
      { q: "Faut-il prévoir des morceaux pour les grands-parents ?", a: "Absolument. Un set 'années 60-70' de 20 min en début de bal fait toujours l'unanimité et libère ensuite la piste pour les plus jeunes." },
      { q: "Combien de morceaux faut-il prévoir pour une soirée de 6h ?", a: "Environ 90-100 titres joués (sur une bibliothèque de réserve 5 fois plus large pour s'adapter en temps réel)." },
    ],
  },
  {
    slug: "dj-vs-groupe-live-mariage-comparatif",
    seoTitle: "DJ ou groupe live pour un mariage : comparatif | DJ Val",
    title: "DJ vs groupe live pour un mariage : le vrai comparatif (prix, ambiance, contraintes)",
    excerpt:
      "Choisir entre un DJ et un groupe live pour son mariage ? Analyse honnête des avantages, limites et coûts de chaque option par un pro.",
    date: "2026-04-10",
    readTime: "7 min",
    category: "Conseils",
    cover: "/__l5e/assets-v1/5df41420-93c2-49cf-8d76-7c1b4272964e/notre-set-up.webp",
    tldr: [
      "DJ : 900-2 800 €, répertoire infini, dancefloor maîtrisé jusqu'à 3h.",
      "Groupe live : 2 500-6 000 €, énergie unique, mais répertoire fini.",
      "Solution hybride : groupe au cocktail/dîner + DJ pour la soirée = combo gagnant.",
    ],
    content: [
      { type: "p", text: "C'est l'un des dilemmes les plus fréquents quand on construit l'animation musicale de son mariage. Voici un comparatif honnête, basé sur 20 ans de terrain et plus de 200 mariages animés." },
      { type: "h2", text: "Prix" },
      { type: "p", text: "Un DJ professionnel se positionne entre 900 € et 2 800 € selon la formule. Un groupe live coûte généralement entre 2 500 € et 6 000 € (3 à 7 musiciens, sonorisation comprise ou non). Sur le strict critère budget, le DJ est imbattable." },
      { type: "h2", text: "Répertoire" },
      { type: "p", text: "Un groupe live propose 40 à 80 morceaux maîtrisés. Un DJ a accès à des millions de titres et peut réagir à toute demande. Pour un mariage multi-générationnel (grands-parents, ados, cousins étrangers), le DJ a l'avantage de la flexibilité totale." },
      { type: "h2", text: "Énergie scénique" },
      { type: "p", text: "Là, le groupe gagne. Voir des musiciens en chair et en os transpirer sur scène crée une énergie inimitable, surtout au cocktail ou en première partie de soirée. Mais cette énergie a une limite : la fatigue. Au-delà de 2-3 h de concert, les sets perdent en intensité." },
      { type: "h2", text: "Durée d'animation" },
      { type: "p", text: "Un groupe joue typiquement 2 à 3 sets de 45 min sur une soirée, avec pauses. Total : 2-3 h de musique live. Un DJ couvre l'intégralité de la soirée sans interruption, jusqu'à 6 h consécutives, idéal pour tenir une piste de danse jusqu'à 3 h du matin." },
      { type: "h2", text: "Contraintes techniques" },
      { type: "p", text: "Un groupe demande une scène, du matériel volumineux (batterie, ampli basse, retours), un temps d'installation 2-3 h, parfois un repas servi pour les musiciens. Un DJ : 3 m² au sol, 1 h d'installation, repas optionnel." },
      { type: "h2", text: "La solution hybride : le combo qui change tout" },
      { type: "p", text: "C'est devenu la formule favorite des mariés exigeants en 2026 : un duo ou trio jazz/folk pour le cocktail (1h30), un DJ qui prend le relais à partir du dîner et tient jusqu'à 3h. Vous avez l'élégance du live ET la durée du DJ." },
      { type: "ul", items: [
        "Coût combiné : 2 500 à 4 500 € total",
        "Énergie live au moment où elle compte le plus (cocktail, photos)",
        "Dancefloor tenu sans interruption en soirée",
        "Souplesse maximale sur le répertoire après 22h",
      ] },
      { type: "h2", text: "Verdict" },
      { type: "p", text: "Si votre priorité est l'ambiance dîner et le côté 'wow' visuel : groupe. Si votre priorité est un dancefloor déchaîné jusqu'à 3h : DJ. Si votre budget le permet (> 2 500 €) : combo live + DJ." },
    ],
    faq: [
      { q: "Un groupe live peut-il enchaîner toute la soirée ?", a: "Très rarement. La fatigue physique limite un groupe à 2-3 sets sur une soirée. Pour une animation 6h, le DJ est techniquement plus adapté." },
      { q: "Le DJ peut-il sonoriser un groupe live ?", a: "Oui, c'est même fréquent en formule hybride. DJ Val sonorise régulièrement le duo cocktail puis enchaîne sur sa propre setlist." },
      { q: "Faut-il prévoir un repas pour les musiciens ?", a: "C'est la coutume pour un groupe (3 à 7 couverts à prévoir). Pour un DJ, c'est laissé à l'appréciation des mariés (généralement un plateau-repas suffit)." },
    ],
  },
  {
    slug: "retroplanning-musique-mariage-12-mois",
    seoTitle: "Rétroplanning musique de mariage sur 12 mois | DJ Val",
    title: "Rétroplanning musique mariage : la checklist mois par mois (J-12 à J+1)",
    excerpt:
      "Quand contacter le DJ, finaliser la playlist, faire le repérage technique, valider la cérémonie ? La timeline complète pour ne rien rater.",
    date: "2026-04-12",
    readTime: "6 min",
    category: "Organisation",
    cover: "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=1200&q=72&auto=format&fit=crop",
    tldr: [
      "J-12 mois : contact DJ et signature acompte.",
      "J-6 mois : rendez-vous préparatoire et repérage du lieu.",
      "J-1 mois : validation playlist et trame finale.",
      "J-1 jour : repérage technique sur place.",
    ],
    content: [
      { type: "p", text: "L'animation musicale est souvent le poste organisé en dernier — à tort. Un bon DJ se réserve 12 mois à l'avance en haute saison. Voici le rétroplanning idéal pour une coordination sans stress." },
      { type: "h2", text: "J-12 mois : choisir et réserver le DJ" },
      { type: "p", text: "C'est le moment de comparer 3 à 5 prestataires, demander des références, faire un rendez-vous découverte. Une fois votre choix fait, signez le contrat et versez l'acompte (généralement 30 %). En juin-septembre en Provence, attendre J-6 mois revient à fortement réduire les options." },
      { type: "h2", text: "J-9 mois : valider le cadre musical général" },
      { type: "p", text: "Échanger avec le DJ sur l'ambiance souhaitée (chic, festif, intimiste), les styles musicaux fil rouge, les éventuels morceaux signature du couple." },
      { type: "h2", text: "J-6 mois : repérage du lieu" },
      { type: "p", text: "Le DJ visite le domaine (ou les domaines si cérémonie et réception sont séparées). Il vérifie l'alimentation électrique, l'accès, l'acoustique, les contraintes sonores éventuelles. Ce repérage évite 90 % des mauvaises surprises le jour J." },
      { type: "h2", text: "J-3 mois : trame détaillée de la soirée" },
      { type: "p", text: "Heure d'arrivée des invités, déroulé cocktail, ordre des plats, ouverture de bal, surprises éventuelles (intervention famille, vidéo, danse coordonnée). Le DJ propose une trame minute par minute." },
      { type: "h2", text: "J-1 mois : validation playlist finale" },
      { type: "p", text: "Liste des must-play (10-15 morceaux à passer absolument), liste des no-go (genres ou artistes à éviter), morceaux clés (entrée des mariés, ouverture de bal, gâteau, dernière danse)." },
      { type: "h2", text: "J-15 jours : briefing prestataires" },
      { type: "p", text: "Le DJ coordonne avec le traiteur (timing service), le photographe (moments clés à capter), le wedding planner (validation trame finale)." },
      { type: "h2", text: "J-1 jour : installation et test technique" },
      { type: "p", text: "Arrivée sur site la veille ou tôt le matin du jour J pour installation complète. Tests sono, lumière, micros, retours. Sur les très grandes installations, montage la veille." },
      { type: "h2", text: "Jour J : déroulé maîtrisé" },
      { type: "p", text: "Arrivée 3h avant le début de la cérémonie ou du cocktail. Test final, dernière vérification avec les mariés, début de prestation. Le DJ devient invisible et seule la fête compte." },
      { type: "h2", text: "J+1 : débrief et souvenirs" },
      { type: "p", text: "Récupération du matériel discrète. Quelques semaines plus tard, partage des photos / vidéos de la prestation pour les mariés." },
    ],
    faq: [
      { q: "Combien de temps à l'avance réserver un DJ en haute saison Provence ?", a: "12 à 18 mois pour un mariage entre mai et septembre dans le Vaucluse, Luberon ou Alpilles. Hors saison, 6 à 9 mois suffisent généralement." },
      { q: "Peut-on changer la playlist à la dernière minute ?", a: "Oui, des ajustements sont toujours possibles jusqu'à J-7 jours. En soirée, le DJ adapte aussi en temps réel selon la réaction du dancefloor." },
      { q: "Le repérage du lieu est-il facturé en plus ?", a: "Chez DJ Val, le repérage est inclus pour les domaines situés dans un rayon de 50 km autour de Cavaillon. Au-delà, un défraiement symbolique peut être appliqué." },
    ],
  },
];

export const POST_SLUGS = POSTS.map((p) => p.slug);
